import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import Logo from "@/components/brand/Logo";
import { Menu, X, Phone, Mail, MessageCircle, Facebook, Instagram, Linkedin, Twitter, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div className="inline-flex items-center rounded-full border border-slate-700/80 overflow-hidden text-[11px] font-bold" data-testid="lang-toggle">
      <button onClick={() => setLang("en")} data-testid="lang-en"
              className={`px-2.5 h-7 ${lang === "en" ? "bg-blue-500 text-white" : "text-slate-300 hover:bg-slate-800"}`}>EN</button>
      <button onClick={() => setLang("es")} data-testid="lang-es"
              className={`px-2.5 h-7 border-l border-slate-700/80 ${lang === "es" ? "bg-blue-500 text-white" : "text-slate-300 hover:bg-slate-800"}`}>ES</button>
    </div>
  );
}

export function MarketingNav() {
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const NAV = [
    { to: "/", label: t("public.nav.home"), end: true },
    { to: "/about", label: t("public.nav.about") },
    { to: "/services", label: t("public.nav.services") },
    { to: "/portfolio", label: t("public.nav.work") },
    { to: "/pricing", label: t("public.nav.packages") },
    { to: "/blog", label: t("public.nav.resources") },
    { to: "/contact", label: t("public.nav.contact") },
  ];
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/70 border-b border-slate-800/60" data-testid="marketing-nav">
      <div className="max-w-7xl mx-auto flex items-center justify-between h-16 px-4 sm:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="marketing-logo"><Logo /></Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "text-blue-400 bg-blue-500/10" : "text-slate-300 hover:text-white hover:bg-slate-800/60"
                }`}
              data-testid={`nav-${n.to.replace('/', '') || 'home'}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LangToggle />
          <div className="text-right leading-tight">
            <div className="text-[10px] uppercase tracking-[0.2em] text-blue-400 font-semibold">{t("public.nav.tagline")}</div>
            <a href="tel:+12562888270" data-testid="nav-phone" className="text-sm text-slate-100 font-semibold hover:text-blue-300">256.288.8270</a>
          </div>
          <Link to="/contact" data-testid="nav-cta" className="btn-scale h-10 px-4 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-blue-500/40">
            {t("public.nav.cta")}
          </Link>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <LangToggle />
          <button className="text-slate-200" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-800 bg-slate-950/95" data-testid="nav-mobile-panel">
          <div className="p-3 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                className={({ isActive }) => `px-3 py-2 rounded text-sm ${isActive ? "text-blue-400 bg-blue-500/10" : "text-slate-300"}`}>
                {n.label}
              </NavLink>
            ))}
            <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-slate-300 border-t border-slate-800 mt-1">{t("public.nav.portal")}</Link>
            <Link to="/contact" onClick={() => setOpen(false)} className="mt-2 mx-3 h-10 grid place-items-center rounded-md bg-blue-500 text-white text-sm font-semibold">
              {t("public.nav.cta")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function MarketingFooter() {
  const [cfg, setCfg] = useState(null);
  const { t } = useLang();
  useEffect(() => { api.get("/public/site-config").then(({ data }) => setCfg(data)).catch(() => setCfg({})); }, []);
  return (
    <footer className="border-t border-slate-800 bg-slate-950 mt-20" data-testid="marketing-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14 grid grid-cols-2 md:grid-cols-6 gap-10">
        <div className="col-span-2">
          <Logo />
          <p className="text-sm text-slate-400 mt-4 leading-relaxed max-w-xs">{t("public.footer.blurb")}</p>
          <div className="text-xs text-slate-500 mt-4 space-y-1">
            <div>{t("public.footer.based")}</div>
            <div>{t("public.footer.serve")}</div>
          </div>
          <div className="flex items-center gap-2 mt-5">
            {[{ Icon: Facebook, href: "#" }, { Icon: Instagram, href: "#" }, { Icon: Linkedin, href: "#" }].map(({ Icon, href }) => (
              <a key={Icon.displayName || Icon.name} href={href} aria-label={Icon.displayName || Icon.name} className="w-9 h-9 grid place-items-center rounded-md border border-slate-800 text-slate-400 hover:text-blue-400 hover:border-blue-500/50 transition-colors">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
          <div className="mt-5 flex flex-col gap-2">
            <Link to="/contact?audit=1" data-testid="footer-cta-audit" className="btn-scale h-10 px-4 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-xs font-bold uppercase tracking-wider text-center">{t("public.footer.audit")}</Link>
            <Link to="/contact/strategy-call" data-testid="footer-cta-call" className="btn-scale h-10 px-4 rounded-md border border-blue-500/50 text-blue-300 text-xs font-bold uppercase tracking-wider text-center hover:bg-blue-500/10">{t("public.footer.call")}</Link>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-4">{t("public.footer.company")}</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><Link to="/about" className="hover:text-white">{t("public.footer.about")}</Link></li>
            <li><Link to="/case-studies" className="hover:text-white">{t("public.footer.cases")}</Link></li>
            <li><Link to="/pricing" className="hover:text-white">{t("public.footer.packages")}</Link></li>
            <li><Link to="/contact" className="hover:text-white">{t("public.nav.contact")}</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-4">{t("public.footer.services")}</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><Link to="/services/website-design" className="hover:text-white">{t("public.footer.links.web")}</Link></li>
            <li><Link to="/services/seo" className="hover:text-white">{t("public.footer.links.seo")}</Link></li>
            <li><Link to="/services/gbp" className="hover:text-white">{t("public.footer.links.gbp")}</Link></li>
            <li><Link to="/services/google-ads" className="hover:text-white">{t("public.footer.links.ads")}</Link></li>
            <li><Link to="/services/call-tracking" className="hover:text-white">{t("public.footer.links.call")}</Link></li>
            <li><Link to="/services/lead-management" className="hover:text-white">{t("public.footer.links.lead")}</Link></li>
            <li><Link to="/services/ai" className="hover:text-white">{t("public.footer.links.ai")}</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-4">{t("public.footer.industries")}</div>
          <ul className="space-y-2 text-sm text-slate-300">
            {(Array.isArray(t("public.industries.list")) ? t("public.industries.list") : ["Contractors","Tree Services","Construction","Medical Clinics","Home Services","Local Businesses"]).map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-4">{t("public.footer.contact")}</div>
          <ul className="space-y-3 text-sm text-slate-300">
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-blue-400" /><a href="tel:+12562888270" className="hover:text-white">256-288-8270</a></li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-blue-400" /><a href="mailto:info@yosimediastudio.com" className="hover:text-white">info@yosimediastudio.com</a></li>
          </ul>
          <div className="text-xs uppercase tracking-wider text-slate-500 mt-6 mb-3">{t("public.footer.legal")}</div>
          <ul className="space-y-2 text-xs text-slate-400">
            <li><Link to="/privacy" className="hover:text-white">{t("public.footer.privacy")}</Link></li>
            <li><Link to="/terms" className="hover:text-white">{t("public.footer.terms")}</Link></li>
            <li><Link to="/cookies" className="hover:text-white">{t("public.footer.cookies")}</Link></li>
            <li><Link to="/payment-policy" className="hover:text-white">{t("public.footer.pay")}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800 py-5 flex flex-wrap gap-2 justify-between items-center max-w-7xl mx-auto px-6 sm:px-8 text-xs text-slate-500">
        <span>{t("public.footer.rights")}</span>
        <span>Huntsville, AL · Alabama &amp; Tennessee</span>
      </div>
    </footer>
  );
}

export default function MarketingLayout({ children }) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col" data-testid="marketing-layout">
      <MarketingNav />
      <main className="flex-1">{children}</main>
      <MarketingFooter />
    </div>
  );
}
